import {callAPI} from '@/store/utils'; // eslint-disable-line
import {base} from './utils';

// Module activation state + config. `GET /settings` and `POST /activation` are
// NOT gated; everything else returns 403 until the module is activated (see the
// API contract in TT-7122). Composed into the `accounting` namespace by
// ./index.js.
export default {
  state: {
    // Full settings object returned by GET /settings (is_activated,
    // currency_iso, bank_journal_enabled, purchase_journal_enabled, default
    // accounts…). `null` until fetched at least once.
    settings: null,
    // Distinguishes "not fetched yet" from "fetched and inactive".
    settingsLoaded: false,
  },
  getters: {
    settings: (state) => state.settings,
    settingsLoaded: (state) => state.settingsLoaded,
    isActivated: (state) => !!state.settings?.is_activated,
    bankJournalEnabled: (state) => !!state.settings?.bank_journal_enabled,
    purchaseJournalEnabled: (state) =>
      !!state.settings?.purchase_journal_enabled,
    // True as soon as at least one journal is enabled — gates the auxiliary
    // accounts / mapping screens.
    anyJournalEnabled: (state) =>
      !!state.settings?.bank_journal_enabled ||
      !!state.settings?.purchase_journal_enabled,
    currencyIso: (state) => state.settings?.currency_iso || null,
  },
  mutations: {
    setSettings(state, data) {
      state.settings = data || null;
      state.settingsLoaded = true;
    },
  },
  actions: {
    // Reads activation state + config. Not gated: always returns a settings
    // object (is_activated:false when the module was never activated).
    async actionGetSettings({commit}) {
      const res = await callAPI('GET', `${base()}/settings`);
      if (res && !res.error) commit('setSettings', res.result);
      return res;
    },
    // Activates the module (idempotent on the backend — never resets
    // activated_at). Activation is triggered from the ops/admin area, not from
    // the main navigation.
    async actionActivate({commit, dispatch}, params = {}) {
      const res = await callAPI('POST', `${base()}/activation`, {
        currency_iso: params.currency_iso || 'EUR',
      });
      if (res && !res.error) {
        commit('setSettings', res.result);
        dispatch(
          'app/actionAddNotification',
          {msg: 'Module comptabilité activé', color: 'green', disappearing: true},
          {root: true},
        );
      }
      return res;
    },
    // Enables/disables the bank or purchase journal (macro toggles).
    // journalType ∈ 'bank' | 'purchase'.
    async actionSetJournalEnabled({commit}, {journalType, enabled}) {
      const res = await callAPI('PUT', `${base()}/journals/${journalType}`, {
        enabled,
      });
      if (res && !res.error) commit('setSettings', res.result);
      return res;
    },
    // Updates config (default accounts, suspense, VAT, currency). Used by the
    // accounting settings screen (L7).
    async actionUpdateSettings({commit}, params) {
      const res = await callAPI('PATCH', `${base()}/settings`, params);
      if (res && !res.error) commit('setSettings', res.result);
      return res;
    },

    // --- Admin/ops: act on an ARBITRARY company (explicit companyId) ---------
    // These target company/<companyId>/accounting directly and DO NOT touch the
    // shared `settings` state (which belongs to the logged-in user's own
    // company). Used by the /admin console to provision accounting for clients.

    // Reads a company's accounting settings (is_activated, bank_journal_enabled,
    // purchase_journal_enabled…). GET /settings is not gated.
    async actionAdminGetSettings(_ctx, {companyId}) {
      return callAPI('GET', `company/${companyId}/accounting/settings`);
    },
    // Activates the accounting module for a company (idempotent; never resets
    // activated_at). Required before a journal can be enabled.
    async actionAdminActivateAccounting(_ctx, {companyId, currencyIso = 'EUR'}) {
      return callAPI('POST', `company/${companyId}/accounting/activation`, {
        currency_iso: currencyIso,
      });
    },
    // Enables/disables a company's bank or purchase journal.
    // journalType ∈ 'bank' | 'purchase'.
    async actionAdminSetJournalEnabled(_ctx, {companyId, journalType, enabled}) {
      return callAPI(
        'PUT',
        `company/${companyId}/accounting/journals/${journalType}`,
        {enabled},
      );
    },
  },
};
